"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useContactEmail } from "@/components/layout/SiteContext";

type Props = { error: Error & { digest?: string }; retry: () => void };

const isDev = process.env.NODE_ENV === "development";

// Turns the raw error into something a person can act on.
function diagnose(error: Error): { title: string; detail: string; devHint?: string } {
  const message = error.message ?? "";
  if (/shopify/i.test(message) || error.name === "ShopifyError") {
    return {
      title: "The shop isn't answering",
      detail: "We couldn't load products from our shop system. This is usually over within a minute or two.",
      devHint:
        "Check SHOPIFY_STORE_DOMAIN, the Storefront API token and its access scopes in .env.local, then restart the dev server.",
    };
  }
  if (/fetch failed|network|ECONN|ENOTFOUND|timeout/i.test(message)) {
    return {
      title: "The connection dropped",
      detail: "The page couldn't reach our servers. Check your internet connection, then try again.",
    };
  }
  if (/chunk|loading css|dynamically imported module/i.test(message)) {
    return {
      title: "This page is out of date",
      detail: "We updated the shop while you had it open. Reload to get the new version.",
    };
  }
  return {
    title: "This page didn't load",
    detail: "Something broke while putting this page together. Trying again often fixes it.",
  };
}

export default function ErrorPage({ error, retry }: Props) {
  const pathname = usePathname();
  const contactEmail = useContactEmail();
  const [time] = useState(() => new Date());
  const { title, detail, devHint } = diagnose(error);

  useEffect(() => {
    console.error(error);
  }, [error]);

  const reference = error.digest ?? "none";
  const report = encodeURIComponent(
    `Page: ${pathname}\nTime: ${time.toISOString()}\nReference: ${reference}\n\nWhat I was doing:\n`,
  );

  return (
    <div className="shell py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="font-serif text-xl italic text-paper">Lost in the post</p>
        <h1 className="mt-2 font-display text-5xl leading-[1.05]">{title}</h1>
        <p className="mt-6 text-lg">{detail}</p>
        <p className="mt-2 text-paper">Your cart is stored separately and hasn&apos;t been touched.</p>

        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <button type="button" className="btn btn-primary" onClick={() => retry()}>
            Try again
          </button>
          <Link href="/" className="link">
            Go to the homepage
          </Link>
        </div>

        <section className="mt-12 rounded-[4px] bg-cocoa p-6" aria-labelledby="error-details">
          <h2 id="error-details" className="font-display text-xl">
            If it keeps happening
          </h2>
          <p className="mt-2 text-[0.9375rem]">
            <a className="link" href={`mailto:${contactEmail}?subject=Problem%20on%20the%20website&body=${report}`}>
              Write to us
            </a>{" "}
            and include these details so we can find the cause.
          </p>
          <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-[0.9375rem]">
            <dt className="text-paper">Page</dt>
            <dd className="break-all font-mono text-[0.875rem]">{pathname}</dd>
            <dt className="text-paper">Time</dt>
            <dd className="font-mono text-[0.875rem]">{time.toISOString()}</dd>
            <dt className="text-paper">Reference</dt>
            <dd className="break-all font-mono text-[0.875rem]">{reference}</dd>
          </dl>
        </section>

        {isDev ? (
          <section className="mt-6 rounded-[4px] border border-gold/40 p-6" aria-label="Developer details">
            <h2 className="font-display text-xl">Developer details</h2>
            <p className="mt-2 break-words font-mono text-[0.875rem]">
              {error.name}: {error.message}
            </p>
            {devHint ? <p className="mt-3 text-[0.9375rem]">{devHint}</p> : null}
            {error.stack ? (
              <details className="mt-3">
                <summary className="text-[0.9375rem] text-paper">Stack trace</summary>
                <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap text-[0.75rem] leading-relaxed">
                  {error.stack}
                </pre>
              </details>
            ) : null}
            <p className="mt-3 text-sm text-paper">Shown in development only.</p>
          </section>
        ) : null}
      </div>
    </div>
  );
}
